import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import {
  Flame, LogOut, Plus, Trash2, Play, Save, Upload, Trophy, Users,
  Zap, MessageSquare, RefreshCw, UserCog, Shield, ShieldOff, Search, Ban, CheckCircle2, Trash,
  UserPlus, Check, XCircle, Clock, Film, Star, Pencil, Heart, Youtube, Settings, Send, Link2, KeyRound,
} from "lucide-react";
import { TempPasswordDialog } from "../components/TempPasswordDialog";
import { api, formatError, resolveAsset } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("tournaments");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" toastOptions={{ className: "glass-dark !border-red-500/30" }} />
      <header className="border-b border-white/5 bg-black/60 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="admin-brand" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-gradient-to-b from-red-500 to-red-900 flex items-center justify-center shadow-[0_0_14px_rgba(230,0,0,0.5)]">
              <Flame size={14} className="text-white" />
            </div>
            <span className="font-display font-extrabold text-lg">
              SPICY<span className="text-red-500">.</span>ADMIN
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-zinc-400 font-mono hidden md:block">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate("/"); }}
              data-testid="admin-logout"
              className="btn-ghost-glass !py-2 !px-4 !text-xs"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: "tournaments", label: "Tournaments", Icon: Trophy },
            { key: "teams", label: "Teams", Icon: Users },
            { key: "matches", label: "Matches", Icon: Zap },
            { key: "requests", label: "Join Requests", Icon: UserPlus },
            { key: "clips", label: "Play of the Week", Icon: Film },
            { key: "feed", label: "Feed", Icon: MessageSquare },
            { key: "users", label: "Users", Icon: UserCog },
            { key: "settings", label: "Settings", Icon: Settings },
          ].map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              data-testid={`admin-tab-${key}`}
              className={`px-5 py-2.5 rounded-full text-sm font-mono uppercase tracking-widest transition-all flex items-center gap-2 whitespace-nowrap ${
                tab === key
                  ? "bg-red-600 text-white shadow-[0_0_18px_rgba(230,0,0,0.5)]"
                  : "glass-dark text-zinc-400 hover:text-white"
              }`}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {tab === "tournaments" && <TournamentsTab />}
        {tab === "teams" && <TeamsTab />}
        {tab === "matches" && <MatchesTab />}
        {tab === "requests" && <JoinRequestsTab />}
        {tab === "clips" && <ClipsTab />}
        {tab === "feed" && <FeedTab />}
        {tab === "users" && <UsersTab />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}

function TournamentsTab() {
  const [list, setList] = useState([]);
  const [teamsByT, setTeamsByT] = useState({});
  const [form, setForm] = useState({
    name: "", description: "", format: "single_elimination",
    prize_pool: 0, game: "", host: "", max_teams: 32, team_size: 1,
    starts_date: "", starts_time: "",
  });
  const [uploadingFor, setUploadingFor] = useState(null);

  const load = async () => {
    const { data } = await api.get("/tournaments");
    setList(data);
    // load teams for each tournament to show winner/runner-up options
    const map = {};
    await Promise.all(
      data.map(async (t) => {
        const r = await api.get(`/tournaments/${t.id}/teams`);
        map[t.id] = r.data;
      })
    );
    setTeamsByT(map);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    if (!form.starts_date || !form.starts_time) { toast.error("Start date and time are required"); return; }
    try {
      const iso = new Date(`${form.starts_date}T${form.starts_time}`).toISOString();
      const { starts_date, starts_time, ...rest } = form;
      await api.post("/tournaments", {
        ...rest,
        prize_pool: Number(form.prize_pool) || 0,
        max_teams: Math.max(2, Math.min(32, Number(form.max_teams) || 32)),
        team_size: Math.max(1, Math.min(5, Number(form.team_size) || 1)),
        starts_at: iso,
      });
      toast.success("Tournament created");
      setForm({ name: "", description: "", format: "single_elimination", prize_pool: 0, game: "", host: "", max_teams: 32, team_size: 1, starts_date: "", starts_time: "" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const updateField = async (id, field, value) => {
    try {
      const payload = { [field]: field === "prize_pool" ? Number(value) : (value === "" ? null : value) };
      await api.put(`/tournaments/${id}`, payload);
      toast.success("Updated");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const generate = async (id) => {
    try {
      await api.post(`/tournaments/${id}/generate`);
      toast.success("Bracket generated");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tournament?")) return;
    await api.delete(`/tournaments/${id}`);
    toast.success("Deleted");
    load();
  };

  const wipeAll = async () => {
    if (!window.confirm("This will permanently delete ALL tournaments, teams, matches and feed items. Users are preserved. Continue?")) return;
    try {
      const { data } = await api.post("/admin/reset-content");
      toast.success(`Cleared ${data.tournaments_deleted} tournaments`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const uploadBanner = async (tid, file) => {
    setUploadingFor(tid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/tournaments/${tid}`, { banner_url: data.url });
      toast.success("Banner uploaded");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setUploadingFor(null);
  };

  return (
    <div className="space-y-6">
      <div className="glass-dark rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="font-display font-bold text-lg">Create Tournament</h3>
          <button
            onClick={wipeAll}
            data-testid="admin-wipe-all"
            className="btn-ghost-glass !py-2 !px-4 !text-xs hover:!text-red-400"
          >
            <Trash2 size={12} /> Clear all sample data
          </button>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <input
            data-testid="t-name" placeholder="Name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
          />
          <input
            data-testid="t-game" placeholder="Game"
            value={form.game} onChange={(e) => setForm({ ...form, game: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
          />
          <input
            data-testid="t-host" placeholder="Host"
            value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
          />
          <select
            data-testid="t-format"
            value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
          >
            <option value="single_elimination">Single Elimination</option>
            <option value="double_elimination">Double Elimination</option>
            <option value="round_robin">Round Robin</option>
            <option value="group_advancement">Group Advancement (Top 2)</option>
          </select>
          <input
            data-testid="t-prize" type="number" placeholder="Prize Pool"
            value={form.prize_pool} onChange={(e) => setForm({ ...form, prize_pool: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
          />
          <input
            data-testid="t-max" type="number" placeholder="Max teams (2-32)" min={2} max={32}
            value={form.max_teams} onChange={(e) => setForm({ ...form, max_teams: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
          />
          <select
            data-testid="t-team-size"
            value={form.team_size}
            onChange={(e) => setForm({ ...form, team_size: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500 text-zinc-200"
          >
            <option value={1}>Solo (1v1)</option>
            <option value={2}>Duo (2v2)</option>
            <option value={3}>Trio (3v3)</option>
            <option value={4}>Squad (4v4)</option>
            <option value={5}>Team (5v5)</option>
          </select>
          <input
            data-testid="t-starts-date" type="date"
            value={form.starts_date} onChange={(e) => setForm({ ...form, starts_date: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500 text-zinc-300"
          />
          <input
            data-testid="t-starts-time" type="time"
            value={form.starts_time} onChange={(e) => setForm({ ...form, starts_time: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500 text-zinc-300"
          />
          <button onClick={create} data-testid="t-create" className="btn-liquid md:col-span-2"><Plus size={16} /> Create</button>
        </div>
        <input
          data-testid="t-desc" placeholder="Description"
          value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="mt-3 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
        />
      </div>

      <div className="space-y-4">
        {list.map((t) => {
          const teams = teamsByT[t.id] || [];
          return (
            <div key={t.id} data-testid={`tournament-row-${t.id}`} className="glass-dark rounded-2xl p-5">
              <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
                <div className="flex items-center gap-3 min-w-0">
                  {t.banner_url && (
                    <img src={resolveAsset(t.banner_url)} alt="" className="h-12 w-20 rounded-lg object-cover border border-white/10" />
                  )}
                  <div className="min-w-0">
                    <div className="font-display font-bold text-xl truncate">{t.name}</div>
                    <div className="text-xs text-zinc-400 font-mono uppercase tracking-widest">
                      {t.format.replace("_", " ")} · {t.status} {t.game ? `· ${t.game}` : ""}
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <label className="btn-ghost-glass !py-2 !px-4 !text-xs cursor-pointer">
                    <Upload size={12} /> {uploadingFor === t.id ? "Uploading..." : "Banner"}
                    <input
                      type="file" accept="image/*" className="hidden"
                      disabled={uploadingFor === t.id}
                      data-testid={`t-banner-${t.id}`}
                      onChange={(e) => e.target.files?.[0] && uploadBanner(t.id, e.target.files[0])}
                    />
                  </label>
                  <button onClick={() => generate(t.id)} data-testid={`generate-${t.id}`} className="btn-ghost-glass !py-2 !px-4 !text-xs">
                    <Play size={14} /> Generate Bracket
                  </button>
                  <button onClick={() => remove(t.id)} data-testid={`delete-t-${t.id}`} className="btn-ghost-glass !py-2 !px-4 !text-xs hover:!text-red-400">
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <LabeledInput label="Name" defaultValue={t.name} onSave={(v) => updateField(t.id, "name", v)} tid={`e-name-${t.id}`} />
                <LabeledInput label="Game" defaultValue={t.game || ""} onSave={(v) => updateField(t.id, "game", v)} tid={`e-game-${t.id}`} />
                <LabeledInput label="Host" defaultValue={t.host || ""} onSave={(v) => updateField(t.id, "host", v)} tid={`e-host-${t.id}`} />
                <LabeledInput label="Prize Pool ($)" type="number" defaultValue={t.prize_pool} onSave={(v) => updateField(t.id, "prize_pool", v)} tid={`e-prize-${t.id}`} />
                <LabeledInput label="Max Teams (2-32)" type="number" defaultValue={t.max_teams || 32} onSave={(v) => updateField(t.id, "max_teams", v)} tid={`e-max-${t.id}`} />
                <LabeledSelect
                  label="Status" defaultValue={t.status} tid={`e-status-${t.id}`}
                  options={["draft", "scheduled", "live", "finished"]}
                  onSave={(v) => updateField(t.id, "status", v)}
                />
                <LabeledInput label="MVP" defaultValue={t.mvp || ""} onSave={(v) => updateField(t.id, "mvp", v)} tid={`e-mvp-${t.id}`} />
                <LabeledInput label="Ended at (ISO)" defaultValue={t.ended_at || ""} onSave={(v) => updateField(t.id, "ended_at", v)} tid={`e-ended-${t.id}`} />
                <LabeledTeamSelect
                  label="Champion" defaultValue={t.winner_team_id || ""} tid={`e-winner-${t.id}`}
                  teams={teams}
                  onSave={(v) => updateField(t.id, "winner_team_id", v)}
                />
                <LabeledTeamSelect
                  label="Runner-up" defaultValue={t.runner_up_team_id || ""} tid={`e-runner-${t.id}`}
                  teams={teams}
                  onSave={(v) => updateField(t.id, "runner_up_team_id", v)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LabeledTeamSelect({ label, defaultValue, teams, onSave, tid }) {
  const [v, setV] = useState(defaultValue || "");
  useEffect(() => setV(defaultValue || ""), [defaultValue]);
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <div className="flex gap-2 mt-1">
        <select
          data-testid={tid} value={v}
          onChange={(e) => { setV(e.target.value); onSave(e.target.value); }}
          className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
        >
          <option value="">— none —</option>
          {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
    </label>
  );
}

function LabeledInput({ label, defaultValue, onSave, type = "text", tid }) {
  const [v, setV] = useState(defaultValue || "");
  useEffect(() => setV(defaultValue || ""), [defaultValue]);
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <div className="flex gap-2 mt-1">
        <input
          data-testid={tid} type={type} value={v} onChange={(e) => setV(e.target.value)}
          className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
        />
        <button onClick={() => onSave(v)} className="btn-ghost-glass !py-2 !px-3"><Save size={14} /></button>
      </div>
    </label>
  );
}

function LabeledSelect({ label, defaultValue, options, onSave, tid }) {
  const [v, setV] = useState(defaultValue || options[0]);
  useEffect(() => setV(defaultValue || options[0]), [defaultValue, options]);
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest text-zinc-500 font-mono">{label}</span>
      <div className="flex gap-2 mt-1">
        <select
          data-testid={tid} value={v}
          onChange={(e) => { setV(e.target.value); onSave(e.target.value); }}
          className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
        >
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    </label>
  );
}

function TeamsTab() {
  const [tournaments, setTournaments] = useState([]);
  const [tid, setTid] = useState("");
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ name: "", color: "#E60000", seed: 0 });
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    const r = await api.get("/tournaments");
    setTournaments(r.data);
    if (!tid && r.data.length) setTid(r.data[0].id);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tid) api.get(`/tournaments/${tid}/teams`).then((r) => setTeams(r.data));
  }, [tid]);

  const add = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    try {
      await api.post(`/tournaments/${tid}/teams`, { ...form, seed: Number(form.seed) });
      toast.success("Team added");
      setForm({ name: "", color: "#E60000", seed: 0 });
      const r = await api.get(`/tournaments/${tid}/teams`);
      setTeams(r.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removeTeam = async (id) => {
    if (!window.confirm("Remove team?")) return;
    await api.delete(`/teams/${id}`);
    toast.success("Removed");
    const r = await api.get(`/tournaments/${tid}/teams`);
    setTeams(r.data);
  };

  const uploadLogo = async (teamId, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.put(`/teams/${teamId}`, { logo_url: data.url });
      toast.success("Logo uploaded");
      const r = await api.get(`/tournaments/${tid}/teams`);
      setTeams(r.data);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    setUploading(false);
  };

  return (
    <div className="space-y-6">
      <div className="glass-dark rounded-2xl p-6">
        <h3 className="font-display font-bold text-lg mb-4">Teams</h3>
        <select
          data-testid="teams-tournament-select"
          value={tid} onChange={(e) => setTid(e.target.value)}
          className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500 mb-4 w-full md:w-auto"
        >
          {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div className="grid md:grid-cols-4 gap-3">
          <input data-testid="team-name" placeholder="Team name"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
          <input data-testid="team-color" type="color"
            value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-2 py-1 h-10" />
          <input data-testid="team-seed" type="number" placeholder="Seed"
            value={form.seed} onChange={(e) => setForm({ ...form, seed: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
          <button onClick={add} data-testid="team-add" className="btn-liquid"><Plus size={16} /> Add Team</button>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {teams.map((t) => (
          <div key={t.id} data-testid={`team-row-${t.id}`} className="glass-dark rounded-xl p-4 flex items-center gap-3">
            {t.logo_url ? (
              <img src={resolveAsset(t.logo_url)} alt="" className="h-12 w-12 rounded-full object-cover border border-white/10" />
            ) : (
              <div className="h-12 w-12 rounded-full flex items-center justify-center font-display font-bold" style={{ background: t.color }}>
                {t.name?.slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{t.name}</div>
              <div className="text-xs text-zinc-400">Seed {t.seed} · {t.wins || 0}W {t.losses || 0}L</div>
            </div>
            <label className="btn-ghost-glass !py-1.5 !px-3 !text-xs cursor-pointer">
              <Upload size={12} /> Logo
              <input
                type="file" accept="image/*" className="hidden"
                disabled={uploading}
                data-testid={`team-upload-${t.id}`}
                onChange={(e) => e.target.files?.[0] && uploadLogo(t.id, e.target.files[0])}
              />
            </label>
            <button onClick={() => removeTeam(t.id)} data-testid={`team-delete-${t.id}`} className="btn-ghost-glass !py-1.5 !px-3 !text-xs hover:!text-red-400">
              <Trash2 size={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MatchesTab() {
  const [tournaments, setTournaments] = useState([]);
  const [tid, setTid] = useState("");
  const [matches, setMatches] = useState([]);
  const [teams, setTeams] = useState([]);

  const load = async () => {
    const r = await api.get("/tournaments");
    setTournaments(r.data);
    const id = tid || r.data[0]?.id;
    if (id) {
      setTid(id);
      const { data } = await api.get(`/tournaments/${id}`);
      setMatches(data.matches);
      setTeams(data.teams);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tid) api.get(`/tournaments/${tid}`).then((r) => { setMatches(r.data.matches); setTeams(r.data.teams); });
  }, [tid]);

  const teamsById = useMemo(() => { const m = {}; teams.forEach((t) => (m[t.id] = t)); return m; }, [teams]);

  const saveMatch = async (m, changes) => {
    try {
      await api.put(`/matches/${m.id}`, changes);
      toast.success("Match updated");
      const { data } = await api.get(`/tournaments/${tid}`);
      setMatches(data.matches);
      setTeams(data.teams);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <div className="glass-dark rounded-2xl p-5 flex items-center gap-3 justify-between flex-wrap">
        <select
          data-testid="matches-tournament-select"
          value={tid} onChange={(e) => setTid(e.target.value)}
          className="bg-black/50 border border-white/10 rounded-lg px-3 py-2"
        >
          {tournaments.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <button onClick={load} className="btn-ghost-glass !py-2 !px-4 !text-xs"><RefreshCw size={12} /> Refresh</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {matches.map((m) => (
          <MatchEditor key={m.id} match={m} teamsById={teamsById} onSave={(c) => saveMatch(m, c)} />
        ))}
      </div>
    </div>
  );
}

function MatchEditor({ match, teamsById, onSave }) {
  const [sa, setSa] = useState(match.score_a);
  const [sb, setSb] = useState(match.score_b);
  const [winner, setWinner] = useState(match.winner_id || "");
  const [status, setStatus] = useState(match.status);
  const [roundName, setRoundName] = useState(match.round_name || "");
  useEffect(() => {
    setSa(match.score_a); setSb(match.score_b);
    setWinner(match.winner_id || ""); setStatus(match.status);
    setRoundName(match.round_name || "");
  }, [match]);

  const ta = teamsById[match.team_a_id];
  const tb = teamsById[match.team_b_id];

  return (
    <div data-testid={`match-editor-${match.id}`} className="glass-dark rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-widest text-zinc-400 font-mono">{roundName}</div>
        <span className={`text-[10px] uppercase font-mono ${
          status === "live" ? "text-red-400" : status === "completed" ? "text-emerald-400" : "text-zinc-500"
        }`}>{status}</span>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center mb-2">
        <div className="truncate">{ta?.name || <span className="text-zinc-600">TBD</span>}</div>
        <input data-testid={`score-a-${match.id}`} type="number" value={sa}
          onChange={(e) => setSa(Number(e.target.value))}
          className="w-16 bg-black/50 border border-white/10 rounded-lg px-2 py-1 text-center font-mono" />
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center mb-3">
        <div className="truncate">{tb?.name || <span className="text-zinc-600">TBD</span>}</div>
        <input data-testid={`score-b-${match.id}`} type="number" value={sb}
          onChange={(e) => setSb(Number(e.target.value))}
          className="w-16 bg-black/50 border border-white/10 rounded-lg px-2 py-1 text-center font-mono" />
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        <select data-testid={`winner-${match.id}`} value={winner} onChange={(e) => setWinner(e.target.value)}
          className="bg-black/50 border border-white/10 rounded-lg px-2 py-1 text-xs">
          <option value="">Auto winner</option>
          {ta && <option value={ta.id}>{ta.name}</option>}
          {tb && <option value={tb.id}>{tb.name}</option>}
        </select>
        <select data-testid={`status-${match.id}`} value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-black/50 border border-white/10 rounded-lg px-2 py-1 text-xs">
          <option value="pending">pending</option>
          <option value="live">live</option>
          <option value="completed">completed</option>
        </select>
      </div>
      <input
        data-testid={`round-name-${match.id}`} value={roundName} placeholder="Round name"
        onChange={(e) => setRoundName(e.target.value)}
        className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-1.5 text-xs mb-3"
      />
      <button
        data-testid={`match-save-${match.id}`}
        onClick={() => onSave({
          score_a: sa, score_b: sb,
          winner_id: winner || null, status, round_name: roundName,
        })}
        className="btn-liquid w-full !py-2 !text-xs"
      >
        <Save size={12} /> Save
      </button>
    </div>
  );
}

function FeedTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", subtitle: "", icon: "bolt" });

  const load = () => api.get("/feed", { params: { limit: 40 } }).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const post = async () => {
    if (!form.title) { toast.error("Title required"); return; }
    try {
      await api.post("/feed", form);
      toast.success("Feed item posted");
      setForm({ title: "", subtitle: "", icon: "bolt" });
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feed item?")) return;
    try {
      await api.delete(`/feed/${id}`);
      toast.success("Feed item deleted");
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  const clearAll = async () => {
    if (!window.confirm("Delete ALL feed items? Tournaments and teams are NOT affected.")) return;
    try {
      const r = await api.delete("/feed");
      toast.success(`Cleared ${r.data.deleted} feed items`);
      setItems([]);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6">
      <div className="glass-dark rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4 gap-3">
          <h3 className="font-display font-bold text-lg">Post Feed Update</h3>
          {items.length > 0 && (
            <button
              onClick={clearAll}
              data-testid="feed-clear-all"
              className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
            >
              <Trash2 size={12} /> Clear all
            </button>
          )}
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          <input data-testid="feed-title" placeholder="Title"
            value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
            className="md:col-span-2 bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
          <select data-testid="feed-icon" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
            className="bg-black/50 border border-white/10 rounded-lg px-3 py-2">
            <option value="bolt">Bolt</option>
            <option value="crown">Crown</option>
            <option value="coin">Coin</option>
            <option value="clock">Clock</option>
            <option value="flame">Flame</option>
          </select>
          <button onClick={post} data-testid="feed-post" className="btn-liquid"><Plus size={16} /> Post</button>
        </div>
        <input data-testid="feed-subtitle" placeholder="Subtitle (optional)"
          value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
          className="mt-3 w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 focus:outline-none focus:border-red-500" />
      </div>
      <div className="space-y-2">
        {items.map((i) => (
          <div key={i.id} data-testid={`feed-row-${i.id}`} className="glass-dark rounded-xl p-4 flex justify-between items-start gap-3 group">
            <div className="min-w-0 flex-1">
              <div className="font-semibold truncate">{i.title}</div>
              {i.subtitle && <div className="text-xs text-zinc-400 mt-0.5">{i.subtitle}</div>}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest whitespace-nowrap">
                {new Date(i.created_at).toLocaleString()}
              </div>
              <button
                onClick={() => remove(i.id)}
                data-testid={`feed-delete-${i.id}`}
                className="opacity-60 group-hover:opacity-100 hover:text-red-400 transition"
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="glass-dark rounded-xl p-6 text-center text-sm text-zinc-500">
            No feed items yet.
          </div>
        )}
      </div>
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [resetResult, setResetResult] = useState(null);

  const resetPassword = async (u) => {
    if (!window.confirm(
      `Reset the password for ${u.email}?\n\nA temporary password will be generated, their current sessions ` +
      "will be signed out, and they'll have to set a new password on next login."
    )) return;
    setBusy(u.id);
    try {
      const r = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetResult(r.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const load = async (query) => {
    try {
      const r = await api.get("/admin/users", { params: query ? { q: query } : {} });
      setUsers(r.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const setRole = async (u, role) => {
    const verb = role === "admin" ? "grant admin to" : "remove admin from";
    if (!window.confirm(`Are you sure you want to ${verb} ${u.email}?`)) return;
    setBusy(u.id);
    try {
      const r = await api.put(`/admin/users/${u.id}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? r.data : x)));
      toast.success(`${u.email} is now ${role}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const setBan = async (u, banned) => {
    const verb = banned ? "ban" : "unban";
    if (!window.confirm(`Are you sure you want to ${verb} ${u.email}?`)) return;
    setBusy(u.id);
    try {
      const r = await api.put(`/admin/users/${u.id}/ban`, { banned });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? r.data : x)));
      toast.success(`${u.email} ${banned ? "banned" : "unbanned"}`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(
      `Permanently DELETE ${u.email}?\n\nThis removes their account and all their teams. ` +
      "They will need to sign up again from scratch."
    )) return;
    setBusy(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success(`${u.email} deleted`);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const admins = users.filter((u) => u.role === "admin").length;

  return (
    <div className="space-y-4">
      <div className="glass-dark rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg">Registered members</h3>
          <p className="text-xs text-zinc-400 mt-1 font-mono uppercase tracking-widest">
            {users.length} total · {admins} admin{admins === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[240px] max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load(q)}
              placeholder="Search email or username…"
              data-testid="users-search-input"
              className="w-full bg-black/50 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-red-500"
            />
          </div>
          <button
            onClick={() => load(q)}
            data-testid="users-search-btn"
            className="btn-ghost-glass !py-2 !px-4 !text-xs"
          >
            Search
          </button>
          {q && (
            <button
              onClick={() => { setQ(""); load(); }}
              className="btn-ghost-glass !py-2 !px-3 !text-xs"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="glass-dark rounded-2xl overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-4 py-3 text-[10px] uppercase tracking-widest text-zinc-500 font-mono border-b border-white/5">
          <div className="col-span-4">User</div>
          <div className="col-span-2">Joined</div>
          <div className="col-span-2">Role</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        <div className="divide-y divide-white/5">
          {users.map((u) => {
            const isMe = me && u.id === me.id;
            const isAdmin = u.role === "admin";
            const isBanned = Boolean(u.banned);
            const initials = (u.username || u.email || "?").slice(0, 2).toUpperCase();
            return (
              <div
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className={`flex flex-wrap md:grid md:grid-cols-12 gap-x-3 gap-y-2 px-4 py-3 items-center text-sm hover:bg-white/[0.02] ${isBanned ? "opacity-70" : ""}`}
              >
                <div className="w-full md:w-auto md:col-span-4 flex items-center gap-3 min-w-0">
                  {u.avatar_url ? (
                    <img src={resolveAsset(u.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[10px] font-bold uppercase ${isBanned ? "bg-zinc-800" : "bg-gradient-to-br from-red-500 to-red-800"}`}>
                      {initials}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">
                      {u.username || u.name || "—"}
                      {isMe && <span className="ml-2 text-[10px] font-mono uppercase text-red-400">You</span>}
                    </div>
                    <div className="text-xs text-zinc-500 truncate">{u.email}</div>
                  </div>
                </div>
                <div className="md:col-span-2 text-xs text-zinc-400 font-mono">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </div>
                <div className="md:col-span-2">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-mono border ${
                      isAdmin
                        ? "bg-red-500/15 text-red-300 border-red-500/30"
                        : "bg-white/5 text-zinc-400 border-white/10"
                    }`}
                  >
                    {isAdmin ? <Shield size={10} /> : null}
                    {u.role}
                  </span>
                </div>
                <div className="md:col-span-1">
                  {isBanned ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-mono border bg-zinc-800 text-zinc-400 border-zinc-700">
                      <Ban size={10} /> banned
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-mono border bg-emerald-500/10 text-emerald-300 border-emerald-500/20">
                      active
                    </span>
                  )}
                </div>
                <div className="w-full md:w-auto md:col-span-3 flex justify-start md:justify-end gap-2 flex-wrap [&>button]:whitespace-nowrap">
                  {isAdmin ? (
                    <button
                      onClick={() => setRole(u, "user")}
                      disabled={busy === u.id || isMe}
                      data-testid={`revoke-admin-${u.id}`}
                      className="btn-ghost-glass !py-1.5 !px-3 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      title={isMe ? "You can't demote yourself" : "Revoke admin"}
                    >
                      <ShieldOff size={12} /> Revoke
                    </button>
                  ) : (
                    <button
                      onClick={() => setRole(u, "admin")}
                      disabled={busy === u.id || isBanned}
                      data-testid={`grant-admin-${u.id}`}
                      className="btn-liquid !py-1.5 !px-3 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                      title={isBanned ? "Unban first" : "Grant admin"}
                    >
                      <Shield size={12} /> Make admin
                    </button>
                  )}
                  {isBanned ? (
                    <button
                      onClick={() => setBan(u, false)}
                      disabled={busy === u.id}
                      data-testid={`unban-${u.id}`}
                      className="btn-ghost-glass !py-1.5 !px-3 !text-xs"
                    >
                      <CheckCircle2 size={12} /> Unban
                    </button>
                  ) : (
                    <button
                      onClick={() => setBan(u, true)}
                      disabled={busy === u.id || isMe || isAdmin}
                      data-testid={`ban-${u.id}`}
                      className="btn-ghost-glass !py-1.5 !px-3 !text-xs !text-red-300 hover:!text-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={isMe ? "You can't ban yourself" : isAdmin ? "Revoke admin role first" : "Ban user"}
                    >
                      <Ban size={12} /> Ban
                    </button>
                  )}
                  <button
                    onClick={() => resetPassword(u)}
                    disabled={busy === u.id || isMe}
                    data-testid={`reset-password-${u.id}`}
                    className="btn-ghost-glass !py-1.5 !px-3 !text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                    title={isMe ? "Use Settings to change your own password" : "Reset password (temporary)"}
                  >
                    <KeyRound size={12} /> Reset PW
                  </button>
                  <button
                    onClick={() => removeUser(u)}
                    disabled={busy === u.id || isMe || isAdmin}
                    data-testid={`delete-user-${u.id}`}
                    className="btn-ghost-glass !py-1.5 !px-3 !text-xs !text-red-400 hover:!text-red-200 disabled:opacity-40 disabled:cursor-not-allowed"
                    title={isMe ? "You can't delete yourself" : isAdmin ? "Revoke admin role first" : "Delete user permanently"}
                  >
                    <Trash size={12} /> Remove
                  </button>
                </div>
              </div>
            );
          })}
          {users.length === 0 && (
            <div className="px-4 py-12 text-center text-sm text-zinc-500">
              No members found.
            </div>
          )}
        </div>
      </div>
      <TempPasswordDialog result={resetResult} onClose={() => setResetResult(null)} />
    </div>
  );
}

function JoinRequestsTab() {
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all"); // pending | all — default to ALL so rejected/approved stay visible after action

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/join-requests`, {
        params: { status: statusFilter },
      });
      setReqs(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  const approve = async (r) => {
    setBusy(r.id);
    try {
      await api.post(`/admin/join-requests/${r.id}/approve`);
      toast.success(`${r.user_username} approved for ${r.tournament_name}`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (r) => {
    const reason = window.prompt(
      `Reject ${r.user_username}'s request for ${r.tournament_name}?\n\n(Optional reason shown to them)`,
      "",
    );
    if (reason === null) return; // cancelled
    setBusy(r.id);
    try {
      await api.post(`/admin/join-requests/${r.id}/reject`, { reason });
      toast.success(`Request rejected`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const deleteReq = async (r) => {
    const ok = window.confirm(
      `Delete this join request from ${r.user_username}?\n\nThe user will be able to apply again.`,
    );
    if (!ok) return;
    setBusy(r.id);
    try {
      await api.delete(`/admin/join-requests/${r.id}`);
      toast.success("Request deleted — user can apply again");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const pendingCount = reqs.filter((r) => r.status === "pending").length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-bold text-xl flex items-center gap-2">
            <UserPlus size={18} className="text-red-400" /> Join Requests
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Users who asked to join a tournament. Approve to create their team and add them to the roster.
          </p>
        </div>
        <div className="flex gap-2">
          {["pending", "all"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              data-testid={`requests-filter-${s}`}
              className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-widest transition-all ${
                statusFilter === s
                  ? "bg-red-600 text-white"
                  : "glass-dark text-zinc-400 hover:text-white"
              }`}
            >
              {s} {s === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>
      </div>

      {reqs.length === 0 ? (
        <div className="glass-dark rounded-2xl p-10 text-center text-zinc-500">
          No {statusFilter === "pending" ? "pending" : ""} join requests.
        </div>
      ) : (
        <div className="space-y-6">
          {(() => {
            // Group requests by tournament so each tournament gets its own
            // visually separated block. Preserves the server-returned order
            // of items within each group (usually newest first).
            const groups = [];
            const byId = new Map();
            for (const r of reqs) {
              const key = r.tournament_id || "__none";
              if (!byId.has(key)) {
                byId.set(key, { tid: key, name: r.tournament_name || "—", game: r.tournament_game || "", items: [] });
                groups.push(byId.get(key));
              }
              byId.get(key).items.push(r);
            }
            return groups.map((g) => {
              const pendingInGroup = g.items.filter((it) => it.status === "pending").length;
              return (
                <div key={g.tid} data-testid={`requests-group-${g.tid}`}>
                  <div className="flex items-center gap-3 mb-3 px-1">
                    <div className="h-px flex-1 bg-gradient-to-r from-red-500/30 to-transparent" />
                    <div className="flex items-baseline gap-2 min-w-0">
                      <span className="font-display font-bold text-sm text-white truncate">{g.name}</span>
                      {g.game && <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{g.game}</span>}
                      <span className="text-[10px] font-mono text-zinc-500">
                        {g.items.length} {g.items.length === 1 ? "request" : "requests"}
                        {pendingInGroup > 0 ? ` · ${pendingInGroup} pending` : ""}
                      </span>
                    </div>
                    <div className="h-px flex-1 bg-gradient-to-l from-red-500/30 to-transparent" />
                  </div>
                  <div className="space-y-2">
                    {g.items.map((r) => {
                      const isPending = r.status === "pending";
                      return (
                        <div
                          key={r.id}
                          data-testid={`join-request-${r.id}`}
                          className="glass-dark rounded-xl p-4 flex items-center gap-4"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {r.user_avatar_url ? (
                              <img
                                src={resolveAsset(r.user_avatar_url)}
                                alt=""
                                className="h-10 w-10 rounded-full object-cover border border-white/10"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-zinc-800 flex items-center justify-center font-display font-bold text-sm">
                                {(r.user_username || "?").slice(0, 2).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold truncate">{r.user_username}</div>
                              <div className="text-[11px] text-zinc-500 truncate">{r.user_email}</div>
                              {(r.ign || r.player_id || r.discord_username) && (
                                <div
                                  data-testid={`join-request-gameinfo-${r.id}`}
                                  className="text-[10px] font-mono text-red-300/90 mt-1 flex flex-wrap gap-x-3 gap-y-0.5"
                                >
                                  {r.ign && <span>IGN: <span className="text-white">{r.ign}</span></span>}
                                  {r.player_id && <span>ID: <span className="text-white">{r.player_id}</span></span>}
                                  {r.discord_username && <span className="text-[#9ca3ff]">Discord: <span className="text-white">{r.discord_username}</span></span>}
                                </div>
                              )}
                              {Array.isArray(r.roster) && r.roster.length > 1 && (
                                <div
                                  data-testid={`join-request-roster-${r.id}`}
                                  className="mt-2 space-y-1 border-l-2 border-red-500/30 pl-3"
                                >
                                  <div className="text-[9px] uppercase tracking-widest text-red-400/80 font-mono">
                                    {r.team_name ? `Team · ${r.team_name}` : "Team Roster"} ({r.roster.length} players)
                                  </div>
                                  {r.roster.slice(1).map((p, idx) => (
                                    <div key={idx} className="text-[10px] font-mono text-zinc-400 flex flex-wrap gap-x-3 gap-y-0.5">
                                      <span className="text-zinc-500">P{idx + 2}</span>
                                      <span>IGN: <span className="text-white">{p.ign}</span></span>
                                      <span>ID: <span className="text-white">{p.player_id}</span></span>
                                      <span className="text-[#9ca3ff]">Discord: <span className="text-white">{p.discord_username}</span></span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="hidden lg:flex flex-col items-end text-[10px] text-zinc-500 font-mono">
                            <span>{formatDate(r.created_at)}</span>
                            {r.resolved_at && (
                              <span className="mt-0.5">
                                {r.status.toUpperCase()} · {formatDate(r.resolved_at)}
                              </span>
                            )}
                          </div>
                          {isPending ? (
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => approve(r)}
                                disabled={busy === r.id}
                                data-testid={`approve-${r.id}`}
                                className="btn-liquid !py-1.5 !px-3 !text-xs flex items-center gap-1.5"
                              >
                                <Check size={12} /> Approve
                              </button>
                              <button
                                onClick={() => reject(r)}
                                disabled={busy === r.id}
                                data-testid={`reject-${r.id}`}
                                className="btn-ghost-glass !py-1.5 !px-3 !text-xs !text-red-300 hover:!text-red-200 flex items-center gap-1.5"
                              >
                                <XCircle size={12} /> Reject
                              </button>
                              <button
                                onClick={() => deleteReq(r)}
                                disabled={busy === r.id}
                                data-testid={`delete-${r.id}`}
                                title="Delete request — user will be able to apply again"
                                className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 shrink-0">
                              <span
                                className={`text-[10px] font-mono tracking-widest px-2 py-1 rounded-full ${
                                  r.status === "approved"
                                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                                    : "bg-red-500/15 text-red-300 border border-red-500/30"
                                }`}
                              >
                                <span className="inline-flex items-center gap-1">
                                  {r.status === "approved" ? <Check size={10} /> : <Clock size={10} />}
                                  {r.status.toUpperCase()}
                                </span>
                              </span>
                              <button
                                onClick={() => deleteReq(r)}
                                disabled={busy === r.id}
                                data-testid={`delete-${r.id}`}
                                title="Delete request — user will be able to apply again"
                                className="h-8 w-8 rounded-full flex items-center justify-center text-zinc-500 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}


function ClipsTab() {
  const [clips, setClips] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0); // 0..1
  const [uploadStage, setUploadStage] = useState(""); // "Initializing" | "Uploading" | "Finalizing"
  const [busy, setBusy] = useState(null);
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null); // clip being edited
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/clips`);
      setClips(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick a video file first");
    if (!title.trim()) return toast.error("Title is required");
    const name = (file.name || "").toLowerCase();
    if (!name.endsWith(".mp4") && !name.endsWith(".mov")) {
      return toast.error("Only .mp4 or .mov files are allowed");
    }
    const MAX_MB = 500;
    if (file.size > MAX_MB * 1024 * 1024) {
      return toast.error(`Video is too large (max ${MAX_MB} MB)`);
    }

    setUploading(true);
    setProgress(0);
    setUploadStage("Initializing");
    let uploadId = null;
    try {
      // 1) Init session
      const initFd = new FormData();
      initFd.append("title", title.trim());
      initFd.append("description", desc.trim());
      initFd.append("filename", file.name);
      initFd.append("size", String(file.size));
      initFd.append("content_type", file.type || "video/mp4");
      const { data: initData } = await api.post(`/clips/upload/init`, initFd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      uploadId = initData.upload_id;
      const CHUNK = initData.chunk_size || 5 * 1024 * 1024;

      // 2) Upload chunks sequentially
      setUploadStage("Uploading");
      let offset = 0;
      let index = 0;
      while (offset < file.size) {
        const end = Math.min(offset + CHUNK, file.size);
        const slice = file.slice(offset, end);
        const fd = new FormData();
        fd.append("chunk", slice, `chunk-${index}`);
        fd.append("chunk_index", String(index));
        await api.post(`/clips/upload/${uploadId}/chunk`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          // No need for axios onUploadProgress — our per-chunk granularity is already smooth.
        });
        offset = end;
        index += 1;
        setProgress(offset / file.size);
      }

      // 3) Complete
      setUploadStage("Finalizing");
      await api.post(`/clips/upload/${uploadId}/complete`);
      toast.success("Clip uploaded — processing in background");
      setTitle("");
      setDesc("");
      setFile(null);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message || "Upload failed");
      // Best-effort abort (ignore errors)
      if (uploadId) {
        try { await api.delete(`/clips/upload/${uploadId}`); } catch {}
      }
    } finally {
      setUploading(false);
      setProgress(0);
      setUploadStage("");
    }
  };

  // Auto-refresh while any clip is still processing so the thumbnail + status update.
  useEffect(() => {
    const anyProcessing = clips.some((c) => c.status === "processing");
    if (!anyProcessing) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clips]);

  const feature = async (c) => {
    setBusy(c.id);
    try {
      await api.put(`/clips/${c.id}/feature`);
      toast.success(`"${c.title}" is now Play of the Week`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.title}" permanently?`)) return;
    setBusy(c.id);
    try {
      await api.delete(`/clips/${c.id}`);
      toast.success("Clip deleted");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(editing.id);
    try {
      await api.put(`/clips/${editing.id}`, { title: editTitle, description: editDesc });
      toast.success("Clip updated");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(null);
    }
  };

  // ---------- YouTube link form ----------
  const [ytTitle, setYtTitle] = useState("");
  const [ytDesc, setYtDesc] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [ytBusy, setYtBusy] = useState(false);

  const submitYouTube = async (e) => {
    e.preventDefault();
    if (!ytUrl.trim()) return toast.error("Paste a YouTube URL first");
    if (!ytTitle.trim()) return toast.error("Title is required");
    setYtBusy(true);
    try {
      await api.post(`/clips/youtube`, {
        title: ytTitle.trim(),
        description: ytDesc.trim(),
        youtube_url: ytUrl.trim(),
      });
      toast.success("YouTube clip added");
      setYtTitle("");
      setYtDesc("");
      setYtUrl("");
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message || "Could not add YouTube clip");
    } finally {
      setYtBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-bold text-xl flex items-center gap-2">
          <Film size={18} className="text-red-400" /> Play of the Week · Clips
        </h2>
        <p className="text-xs text-zinc-500 mt-1">Upload MP4/MOV clips — up to 500&nbsp;MB. Supports iPhone 4K 60fps (~60-80&nbsp;s) or 1080p up to ~2&nbsp;min. Thumbnails generate automatically.</p>
      </div>

      <form onSubmit={upload} className="glass-dark rounded-2xl p-5 space-y-3" data-testid="clip-upload-form">
        <div className="grid md:grid-cols-2 gap-3">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Clip title"
            maxLength={120}
            data-testid="clip-title-input"
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
          />
          <input
            type="file"
            accept="video/mp4,video/quicktime,.mp4,.mov"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="clip-file-input"
            className="w-full text-xs file:mr-3 file:bg-red-600 file:border-0 file:text-white file:font-bold file:text-[11px] file:px-3 file:py-2 file:rounded-full file:cursor-pointer text-zinc-300"
          />
        </div>
        {file && !uploading && (
          <div className="text-[11px] font-mono text-zinc-400">
            Selected: <span className="text-zinc-200">{file.name}</span> · {(file.size / (1024 * 1024)).toFixed(1)}&nbsp;MB
          </div>
        )}
        <textarea
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          placeholder="Short description (who, what, when)"
          maxLength={800}
          rows={2}
          data-testid="clip-desc-input"
          className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
        />
        {uploading && (
          <div data-testid="clip-upload-progress" className="space-y-1.5">
            <div className="flex items-center justify-between text-[11px] font-mono text-zinc-400">
              <span>{uploadStage || "Uploading"}{uploadStage === "Uploading" && file ? ` · ${(file.size / (1024 * 1024)).toFixed(1)} MB` : ""}</span>
              <span className="text-red-300">{Math.round(progress * 100)}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-red-600 to-red-400 transition-[width] duration-200"
                style={{ width: `${Math.max(3, Math.round(progress * 100))}%` }}
              />
            </div>
          </div>
        )}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={uploading || !file || !title.trim()}
            data-testid="clip-upload-submit"
            className="btn-liquid !py-2 !px-5 !text-xs"
          >
            <Upload size={12} /> {uploading ? (uploadStage || "Uploading...") : "Upload clip"}
          </button>
        </div>
      </form>

      {/* ---------- YouTube link form ---------- */}
      <form onSubmit={submitYouTube} className="glass-dark rounded-2xl p-5 space-y-3" data-testid="clip-youtube-form">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-mono text-red-300">
          <Youtube size={14} className="text-red-400" />
          Add via YouTube link
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <input
            value={ytTitle}
            onChange={(e) => setYtTitle(e.target.value)}
            placeholder="Clip title"
            maxLength={120}
            data-testid="clip-youtube-title"
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
          />
          <input
            value={ytUrl}
            onChange={(e) => setYtUrl(e.target.value)}
            placeholder="https://youtu.be/… or https://youtube.com/watch?v=…"
            maxLength={500}
            data-testid="clip-youtube-url"
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500 font-mono"
          />
        </div>
        <textarea
          value={ytDesc}
          onChange={(e) => setYtDesc(e.target.value)}
          placeholder="Short description (optional)"
          maxLength={800}
          rows={2}
          data-testid="clip-youtube-desc"
          className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-red-500"
        />
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={ytBusy || !ytUrl.trim() || !ytTitle.trim()}
            data-testid="clip-youtube-submit"
            className="btn-liquid !py-2 !px-5 !text-xs"
          >
            <Youtube size={12} /> {ytBusy ? "Adding…" : "Add YouTube clip"}
          </button>
        </div>
      </form>

      {clips.length === 0 ? (
        <div className="glass-dark rounded-2xl p-10 text-center text-zinc-500">No clips yet.</div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clips.map((c) => (
            <div key={c.id} data-testid={`clip-row-${c.id}`} className={`glass-dark rounded-xl overflow-hidden flex flex-col ${c.status === "failed" ? "ring-1 ring-red-500/40" : ""}`}>
              <div className="aspect-video bg-black/60 relative">
                {c.thumb_url ? (
                  <img src={resolveAsset(c.thumb_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                ) : c.status === "processing" ? (
                  <div className="w-full h-full grid place-items-center text-zinc-300">
                    <div className="text-center">
                      <div className="mx-auto h-8 w-8 border-2 border-red-500/40 border-t-red-400 rounded-full animate-spin mb-2" />
                      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-400">Processing…</div>
                    </div>
                  </div>
                ) : c.status === "failed" ? (
                  <div className="w-full h-full grid place-items-center p-3">
                    <div className="text-center">
                      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-red-300 mb-1">Upload failed</div>
                      <div className="text-[10px] text-zinc-500 truncate max-w-[220px]" title={c.error || ""}>{c.error || "unknown error"}</div>
                    </div>
                  </div>
                ) : (
                  <div className="w-full h-full grid place-items-center text-zinc-600 text-xs">NO PREVIEW</div>
                )}
                {c.featured && (
                  <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-yellow-400/20 border border-yellow-400/40 text-yellow-200">
                    <Star size={9} fill="currentColor" /> Featured
                  </span>
                )}
                {c.source_type === "youtube" && (
                  <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.2em] px-1.5 py-0.5 rounded-full bg-red-600/90 border border-red-400/60 text-white">
                    <Youtube size={9} /> YouTube
                  </span>
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col gap-2">
                <div className="font-semibold truncate">{c.title}</div>
                <div className="flex items-center gap-3 text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
                  <span className="flex items-center gap-1"><Heart size={10} /> {c.like_count || 0}</span>
                  <span className="flex items-center gap-1"><MessageSquare size={10} /> {c.comment_count || 0}</span>
                </div>
                <div className="mt-auto flex items-center gap-2 pt-2">
                  {c.status !== "processing" && c.status !== "failed" && !c.featured && (
                    <button
                      onClick={() => feature(c)}
                      disabled={busy === c.id}
                      data-testid={`clip-feature-${c.id}`}
                      className="btn-liquid !py-1.5 !px-3 !text-[11px]"
                    >
                      <Star size={11} /> Feature
                    </button>
                  )}
                  {c.status !== "processing" && (
                    <button
                      onClick={() => { setEditing(c); setEditTitle(c.title); setEditDesc(c.description || ""); }}
                      data-testid={`clip-edit-${c.id}`}
                      className="btn-ghost-glass !py-1.5 !px-3 !text-[11px]"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  )}
                  <button
                    onClick={() => remove(c)}
                    disabled={busy === c.id}
                    data-testid={`clip-delete-${c.id}`}
                    className="btn-ghost-glass !py-1.5 !px-3 !text-[11px] !text-red-300 ml-auto"
                  >
                    <Trash2 size={11} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur grid place-items-center z-50 p-4" onClick={() => setEditing(null)}>
          <div className="glass-dark rounded-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-lg mb-3">Edit clip</h3>
            <input
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              maxLength={120}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:border-red-500"
            />
            <textarea
              value={editDesc}
              onChange={(e) => setEditDesc(e.target.value)}
              rows={4}
              maxLength={800}
              className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm mb-3 focus:outline-none focus:border-red-500"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost-glass !py-1.5 !px-4 !text-xs">Cancel</button>
              <button
                onClick={saveEdit}
                disabled={busy === editing.id || !editTitle.trim()}
                className="btn-liquid !py-1.5 !px-4 !text-xs"
              >
                <Save size={12} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



function SettingsTab() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [webhookInput, setWebhookInput] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/settings");
      setSettings(res.data);
      setWebhookInput(res.data?.discord_webhook_url || "");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const patch = async (body) => {
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", body);
      setSettings(res.data);
      toast.success("Settings updated");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveWebhook = async () => {
    const url = (webhookInput || "").trim();
    if (url && !/^https:\/\/(ptb\.|canary\.)?discord(app)?\.com\/api\/webhooks\//i.test(url)) {
      toast.error("That doesn't look like a Discord webhook URL");
      return;
    }
    await patch({ discord_webhook_url: url || "" });
  };

  const toggleFlag = async (key) => {
    const next = !(settings?.[key] ?? true);
    await patch({ [key]: next });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const res = await api.post("/admin/settings/test-discord", {
        discord_webhook_url: (webhookInput || "").trim() || null,
      });
      if (res.data?.ok) toast.success("Test message sent to Discord ✓");
      else toast.error(res.data?.error || "Test failed");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="text-zinc-500 text-sm font-mono" data-testid="settings-loading">Loading settings...</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-tab">
      <div className="glass-dark rounded-2xl p-6 border border-white/5">
        <div className="flex items-center gap-3 mb-1">
          <div className="h-9 w-9 rounded-lg bg-[#5865F2]/20 border border-[#5865F2]/40 flex items-center justify-center">
            <MessageSquare size={16} className="text-[#7e88ff]" />
          </div>
          <div>
            <h2 className="font-display font-bold text-lg">Discord Webhook</h2>
            <p className="text-xs text-zinc-500 font-mono">Auto-post tournament events to your Discord channel</p>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
              Webhook URL
            </label>
            <div className="flex gap-2">
              <div className="flex-1 flex items-center glass-dark rounded-lg border border-white/10 focus-within:border-red-500/50 transition-colors">
                <Link2 size={14} className="text-zinc-500 ml-3 shrink-0" />
                <input
                  type="url"
                  value={webhookInput}
                  onChange={(e) => setWebhookInput(e.target.value)}
                  placeholder="https://discord.com/api/webhooks/..."
                  data-testid="settings-discord-webhook-input"
                  className="flex-1 bg-transparent px-3 py-2.5 text-sm font-mono outline-none text-white placeholder:text-zinc-600"
                />
              </div>
              <button
                onClick={saveWebhook}
                disabled={saving}
                data-testid="settings-save-webhook-btn"
                className="btn-primary-red !py-2.5 !px-4 !text-xs"
              >
                <Save size={14} /> Save
              </button>
            </div>
            <p className="text-[11px] text-zinc-600 font-mono mt-2">
              Server Settings → Integrations → Webhooks → New Webhook → Copy URL
            </p>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white leading-snug">Discord notifications</div>
              <div className="text-xs text-zinc-500 font-mono leading-snug">Master toggle — turn off to silence everything</div>
            </div>
            <IosToggle
              testid="settings-toggle-discord-enabled"
              on={settings?.discord_enabled ?? true}
              disabled={saving}
              onChange={() => toggleFlag("discord_enabled")}
            />
          </div>

          <button
            onClick={sendTest}
            disabled={testing || saving}
            data-testid="settings-send-test-btn"
            className="btn-ghost-glass !py-2.5 !px-4 !text-xs w-full justify-center"
          >
            {testing ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
            {testing ? "Sending..." : "Send test message"}
          </button>
        </div>
      </div>

      <AnnouncementComposer />
    </div>
  );
}

/**
 * iOS-style toggle pill. Off = solid dark grey with thumb on the left.
 * On = solid iOS-green with thumb on the right. Uses inline-style colors
 * so the bg always renders even if the Tailwind JIT misses a class.
 */
function IosToggle({ on, disabled, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      data-testid={testid}
      disabled={disabled}
      onClick={onChange}
      className="relative shrink-0 inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-60"
      style={{
        width: 48,
        height: 28,
        backgroundColor: on ? "#e60000" : "#39393d",
        boxShadow: "inset 0 0 1px rgba(0,0,0,0.4)",
      }}
    >
      <span
        className="absolute top-1/2 -translate-y-1/2 rounded-full bg-white transition-transform duration-200"
        style={{
          width: 24,
          height: 24,
          left: 2,
          transform: `translate3d(${on ? 20 : 0}px, -50%, 0)`,
          boxShadow: "0 2px 4px rgba(0,0,0,0.35), 0 0 0 0.5px rgba(0,0,0,0.05)",
        }}
      />
    </button>
  );
}

function AnnouncementComposer() {
  const [content, setContent] = useState("");
  const [title, setTitle] = useState("");
  const [asEmbed, setAsEmbed] = useState(false); // default plain message for big text
  const [sending, setSending] = useState(false);

  const send = async () => {
    const body = content.trim();
    if (!body) { toast.error("Write a message first"); return; }
    setSending(true);
    try {
      const res = await api.post("/admin/settings/send-announcement", {
        content: body,
        title: title.trim() || null,
        as_embed: asEmbed,
      });
      if (res.data?.ok) {
        toast.success("Announcement sent to Discord ✓");
        setContent("");
        setTitle("");
      } else {
        toast.error(res.data?.error || "Failed to send");
      }
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSending(false);
    }
  };

  const loadTemplate = () => {
    setTitle("🎉 New Tournament Starting Soon!");
    setContent(
      "⏰ **Limited Time Only**\n\n" +
      "Get ready — a new tournament is about to kick off. Don't miss your chance to prove yourself and climb the leaderboard.\n\n" +
      "**📋 Tournament Details**\n" +
      "- Format: Single Elimination\n" +
      "- Entry: Free\n" +
      "- Prize Pool: **$100**\n\n" +
      "**🎟️ How to Join**\n" +
      "- Head to <https://spicycommunity.online>\n" +
      "- Sign in and click **Request to join**\n" +
      "- Wait for admin approval\n\n" +
      "🔥 See you in the bracket!"
    );
  };

  const remaining = 1990 - content.length;

  return (
    <div className="glass-dark rounded-2xl p-6 border border-white/5" data-testid="announcement-composer">
      <div className="flex items-center gap-3 mb-1">
        <div className="h-9 w-9 rounded-lg bg-red-500/20 border border-red-500/40 flex items-center justify-center">
          <MessageSquare size={16} className="text-red-400" />
        </div>
        <div>
          <h3 className="font-display font-bold text-base">Announcement composer</h3>
          <p className="text-xs text-zinc-500 font-mono">Write a message and send it straight to your Discord channel</p>
        </div>
      </div>

      <div className="mt-5 space-y-3">
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/[0.02] border border-white/5">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white leading-snug">Send as embed card</div>
            <div className="text-xs text-zinc-500 font-mono leading-snug">
              {asEmbed
                ? "ON · small card with red sidebar"
                : "OFF · big full-width message like a real announcement"}
            </div>
          </div>
          <IosToggle
            testid="announcement-toggle-embed"
            on={asEmbed}
            onChange={() => setAsEmbed(!asEmbed)}
          />
        </div>

        <button
          type="button"
          onClick={loadTemplate}
          data-testid="announcement-template-btn"
          className="btn-ghost-glass !py-2 !px-3 !text-xs w-full justify-center"
        >
          ✨ Load tournament template
        </button>

        <div>
          <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
            Title (optional)
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value.slice(0, 120))}
            placeholder="🎉 New Tournament Starting Soon!"
            data-testid="announcement-title-input"
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-500 text-white placeholder:text-zinc-600"
          />
          <p className="text-[11px] text-zinc-600 font-mono mt-1.5">
            {asEmbed ? "Shown as the embed title" : "Rendered as # big heading in Discord"}
          </p>
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
            Message
          </label>
          <textarea
            rows={8}
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, 1990))}
            placeholder={"⏰ **Limited Time Only**\n\nHey everyone, bracket is live — check in before 7 PM ET!\n\n**📋 Details**\n- Format: Single Elim\n- Prize: $100"}
            data-testid="announcement-content-input"
            className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-red-500 text-white placeholder:text-zinc-600 resize-none font-mono"
          />
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-[11px] text-zinc-600 font-mono">
              Markdown: **bold** · *italic* · # heading · - bullet
            </span>
            <span className={`text-[11px] font-mono ${remaining < 100 ? "text-red-400" : "text-zinc-600"}`}>
              {remaining} chars left
            </span>
          </div>
        </div>
        <button
          onClick={send}
          disabled={sending || !content.trim()}
          data-testid="announcement-send-btn"
          className="btn-primary-red w-full justify-center !py-2.5 !text-sm disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? <RefreshCw size={14} className="animate-spin" /> : <Send size={14} />}
          {sending ? "Sending..." : "Send announcement to Discord"}
        </button>
      </div>
    </div>
  );
}

